import React from 'react';
import { 
  Grid, 
  GridItem, 
  Box, 
  Container, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Card, 
  CardBody, 
  Flex, 
  Icon,
  SimpleGrid,
  Center,
  Badge,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import HeaderMenus from './HeaderMenus';
import { 
  FiFileText, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiTrendingUp,
  FiCpu,
  FiShield,
  FiSend,
  FiDatabase,
  FiZap,
  FiTarget,
  FiBarChart,
  FiSearch,
  FiMessageSquare,
  FiDollarSign,
  FiClock,
  FiAward,
  FiX
} from 'react-icons/fi';

const RevedyDemo = () => {
  const steps = [
    {
      id: 0,
      title: "Clinical Documentation Intelligence",
      icon: FiFileText,
      bgGradient: "linear(to-r, blue.400, blue.600)",
      benefitTag: "Capture 100% of Justified Revenue",
      problems: [
        "Documentation gaps leading to denials",
        "Leaving money on the table unknowingly",
        "CPT coding inaccuracy"
      ]
    },
    {
      id: 1,
      title: "Predictive Compliance Engine",
      icon: FiCpu,
      bgGradient: "linear(to-r, purple.400, purple.600)",
      benefitTag: "Know You'll Get Paid Before You Submit",
      problems: [
        "Surprise denials for medical necessity",
        "Guessing what payers will accept",
        "Missing diagnoses justified by clinical evidence"
      ]
    },
    {
      id: 2,
      title: "Real-Time Payer Intelligence",
      icon: FiTrendingUp,
      bgGradient: "linear(to-r, green.400, green.600)",
      benefitTag: "Turn Denials into Actionable Insights",
      problems: [
        "Not knowing why claims were denied",
        "Missing critical response deadlines",
        "Flying blind on payer patterns"
      ]
    },
    {
      id: 3,
      title: "Intelligent Appeals Automation",
      icon: FiSend,
      bgGradient: "linear(to-r, orange.400, orange.600)",
      benefitTag: "Level the Playing Field and Win More Appeals",
      problems: [
        "Generic appeals that don't address specific issues",
        "Lost revenue from unappealed denials",
        "Hours spent crafting appeals"
      ]
    }
  ];

  const StepCard = ({ step, stepNumber }) => (
    <Card shadow="xl" h="full" borderTop="4px" borderTopColor={step.bgGradient.includes('blue') ? 'blue.400' : step.bgGradient.includes('purple') ? 'purple.400' : step.bgGradient.includes('green') ? 'green.400' : 'orange.400'}>
      <CardBody p={6}>
        <VStack spacing={6} h="full">
          {/* Step Number */}
          <Badge colorScheme="gray" fontSize="sm" px={3} py={1}>
            Step {stepNumber}
          </Badge>

          {/* Benefit Tag */}
          <Box textAlign="center">
            <Text fontSize="sm" fontWeight="bold" color="green.600" textTransform="uppercase" letterSpacing="wide">
              {step.benefitTag}
            </Text>
          </Box>

          {/* Main Icon and Title */}
          <VStack spacing={4} flex={1} justify="center">
            <Box
              p={8}
              borderRadius="2xl"
              bgGradient={step.bgGradient}
              color="white"
              shadow="lg"
            >
              <Icon as={step.icon} boxSize={16} />
            </Box>
            <Heading size="md" color="gray.800" textAlign="center" lineHeight="short">
              {step.title}
            </Heading>
          </VStack>

          {/* Problems We Solve */}
          <VStack spacing={3} w="full" align="stretch">
            <Text fontSize="sm" fontWeight="semibold" color="red.600" textAlign="center" textTransform="uppercase" letterSpacing="wide">
              Problems We Solve
            </Text>
            <List spacing={2}>
              {step.problems.map((problem, idx) => (
                <ListItem key={idx} fontSize="sm" color="gray.600">
                  <ListIcon as={FiX} color="red.400" />
                  {problem}
                </ListItem>
              ))}
            </List>
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );

  return (
    <Grid
      templateAreas={`"header header"
                      "main main"`}
      gridTemplateRows={'60px 1fr'}
      gridTemplateColumns={'1fr'}
      h='100vh'
      gap='1'
    >
      <HeaderMenus />
      <GridItem area={'main'} bg="gray.50" overflowY="auto">
        <Container maxW="7xl" py={12}>
          {/* Header */}
          <VStack spacing={6} mb={12} textAlign="center">
            <Heading size="2xl" color="gray.800">
              See, Predict, and Capture Your True Revenue Potential
            </Heading>
            <Badge colorScheme="blue" fontSize="lg" px={4} py={2} borderRadius="full">
              The Revenue AI Platform
            </Badge>
            <Text fontSize="xl" color="gray.600" maxW="4xl">
              Transform your medical billing with intelligent automation that captures every justified dollar, 
              predicts payer responses, and turns denials into approvals.
            </Text>
          </VStack>

          {/* Four Steps Grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} mb={12}>
            {steps.map((step, index) => (
              <StepCard key={step.id} step={step} stepNumber={index + 1} />
            ))}
          </SimpleGrid>

          {/* Intelligence Flow */}
          <Card mb={12} bg="blue.50" borderLeft="4px" borderLeftColor="blue.400">
            <CardBody p={8}>
              <VStack spacing={4} textAlign="center">
                <Heading size="md" color="blue.800">
                  Complete Revenue Intelligence
                </Heading>
                <HStack spacing={8} justify="center" wrap="wrap">
                  <VStack>
                    <Icon as={FiBarChart} boxSize={6} color="blue.600" />
                    <Text fontSize="sm" color="blue.700" fontWeight="semibold">Visibility</Text>
                    <Text fontSize="xs" color="blue.600">See every opportunity</Text>
                  </VStack>
                  <Text color="blue.400" fontSize="2xl">→</Text>
                  <VStack>
                    <Icon as={FiTarget} boxSize={6} color="blue.600" />
                    <Text fontSize="sm" color="blue.700" fontWeight="semibold">Prediction</Text>
                    <Text fontSize="xs" color="blue.600">Know outcomes before submission</Text>
                  </VStack>
                  <Text color="blue.400" fontSize="2xl">→</Text>
                  <VStack>
                    <Icon as={FiZap} boxSize={6} color="blue.600" />
                    <Text fontSize="sm" color="blue.700" fontWeight="semibold">Action</Text>
                    <Text fontSize="xs" color="blue.600">Automated optimization</Text>
                  </VStack>
                  <Text color="blue.400" fontSize="2xl">→</Text>
                  <VStack>
                    <Icon as={FiDollarSign} boxSize={6} color="blue.600" />
                    <Text fontSize="sm" color="blue.700" fontWeight="semibold">Results</Text>
                    <Text fontSize="xs" color="blue.600">Increased collections</Text>
                  </VStack>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Value Propositions */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mb={12}>
            <Card shadow="lg" textAlign="center" borderTop="4px" borderTopColor="blue.400">
              <CardBody py={8}>
                <VStack spacing={4}>
                  <Center w={20} h={20} bg="blue.100" borderRadius="full">
                    <Icon as={FiZap} boxSize={10} color="blue.600" />
                  </Center>
                  <Heading size="md" color="gray.800">Lightning Fast Intelligence</Heading>
                  <Text color="gray.600">Process 100+ cases in the time it takes to do 1 manually</Text>
                </VStack>
              </CardBody>
            </Card>

            <Card shadow="lg" textAlign="center" borderTop="4px" borderTopColor="green.400">
              <CardBody py={8}>
                <VStack spacing={4}>
                  <Center w={20} h={20} bg="green.100" borderRadius="full">
                    <Icon as={FiShield} boxSize={10} color="green.600" />
                  </Center>
                  <Heading size="md" color="gray.800">Audit-Ready Compliance</Heading>
                  <Text color="gray.600">Every claim backed by complete clinical evidence</Text>
                </VStack>
              </CardBody>
            </Card>

            <Card shadow="lg" textAlign="center" borderTop="4px" borderTopColor="purple.400">
              <CardBody py={8}>
                <VStack spacing={4}>
                  <Center w={20} h={20} bg="purple.100" borderRadius="full">
                    <Icon as={FiTrendingUp} boxSize={10} color="purple.600" />
                  </Center>
                  <Heading size="md" color="gray.800">Proven Revenue Growth</Heading>
                  <Text color="gray.600">Increase collections by 20-30% without changing how you practice</Text>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* CTA */}
          <Center>
            <Card shadow="2xl" bgGradient="linear(to-r, blue.500, purple.500)" maxW="2xl">
              <CardBody p={10}>
                <VStack spacing={6} textAlign="center">
                  <Heading size="lg" color="white">
                    Ready to See Your Revenue Potential?
                  </Heading>
                  <Text color="blue.100" fontSize="lg">
                    Discover how much revenue you're leaving on the table and how Revedy can help you capture it
                  </Text>
                  <HStack spacing={6}>
                    <Box
                      as="button"
                      bg="white"
                      color="blue.600"
                      px={8}
                      py={4}
                      borderRadius="xl"
                      fontWeight="bold"
                      fontSize="lg"
                      shadow="lg"
                      _hover={{ 
                        shadow: "xl", 
                        transform: "scale(1.05)" 
                      }}
                      transition="all 0.3s"
                    >
                      Get Revenue Analysis
                    </Box>
                    <Box
                      as="button"
                      border="2px"
                      borderColor="white"
                      color="white"
                      px={8}
                      py={4}
                      borderRadius="xl"
                      fontWeight="bold"
                      fontSize="lg"
                      _hover={{ 
                        bg: "whiteAlpha.200",
                        transform: "scale(1.05)" 
                      }}
                      transition="all 0.3s"
                    >
                      Schedule Demo
                    </Box>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          </Center>
        </Container>
      </GridItem>
    </Grid>
  );
};

export default RevedyDemo;
